/**
 * InspectionQueueWidget — Collapsible card at the top of the Requests page.
 * Shows only jobs whose `estimate_status === "Estimate Pending Review"`.
 * Clicking a row opens the existing Job Details dialog and forces the
 * Inspection tab via the `defaultTab` prop on RequestMonitoring.
 */
import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, ClipboardCheck, RefreshCw } from "lucide-react";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
};

const fmtMoney = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

export default function InspectionQueueWidget({ onOpenJob, refreshKey }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/management/inspection-queue?limit=50");
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("inspection queue failed", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (total === 0 && !loading) return null;

  return (
    <Card className="border border-amber-300 bg-amber-50/40" data-testid="inspection-queue-widget">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}
        className="w-full flex items-center justify-between gap-2 p-3 hover:bg-amber-50 rounded-t-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400"
        data-testid="inspection-queue-toggle"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-amber-700" /> : <ChevronRight className="w-4 h-4 text-amber-700" />}
          <ClipboardCheck className="w-4 h-4 text-amber-700" />
          <span className="text-sm font-semibold text-amber-900">
            Inspection Queue — Estimates Pending Review
          </span>
          <span className="ml-1 inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full bg-amber-600 text-white text-[11px] font-bold"
                data-testid="inspection-queue-count">
            {total}
          </span>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); load(); }}
          className="h-7 w-7 p-0 hover:bg-amber-100"
          data-testid="inspection-queue-refresh"
          aria-label="Refresh inspection queue"
        >
          <RefreshCw className={`w-3.5 h-3.5 text-amber-700 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {open && (
        <div className="border-t border-amber-200 divide-y divide-amber-100">
          {loading && items.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-amber-700">Loading…</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-amber-700">No estimates pending review.</div>
          ) : items.map((it) => (
            <button
              key={it.request_id}
              type="button"
              onClick={() => onOpenJob?.(it.request_id, "inspection")}
              className="w-full text-left px-3 py-2.5 hover:bg-amber-100/60 transition-colors flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3"
              data-testid={`inspection-queue-row-${it.request_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-zinc-900 truncate">
                    {it.customer_name || "—"}
                  </span>
                  {it.damage_type && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">
                      {it.damage_type}
                    </span>
                  )}
                  <span className="font-mono text-[10px] text-zinc-400">{it.request_code}</span>
                </div>
                <div className="text-[11px] text-zinc-600 truncate">
                  {it.property_address_line1}{it.property_city ? `, ${it.property_city}` : ""}{it.property_state ? `, ${it.property_state}` : ""}
                </div>
                <div className="text-[11px] text-zinc-500">
                  Tech: <span className="text-zinc-700">{it.technician_name || "Unassigned"}</span> · Submitted {fmtTime(it.submitted_at)}
                </div>
              </div>
              <div className="flex sm:flex-col sm:items-end gap-2 sm:gap-0">
                <span className="text-base font-bold text-zinc-900" data-testid={`inspection-queue-amount-${it.request_id}`}>
                  {fmtMoney(it.preliminary_estimate)}
                </span>
                <span className="text-[10px] uppercase tracking-wide text-amber-700 font-bold">
                  Review →
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}
